// ─── NOTIFICAÇÃO BELL + CENTER (Sprint 3.0) ────────────────────────────────────
// Substitui o sino estático do header (AppLayout.tsx, ponto fixo/sempre
// visível desde o scaffold) por dados reais. Dropdown com
// abrir/fechar-por-clique-fora — mesma técnica já usada pelos Autocompletes
// (PessoaAutocomplete etc.), não Radix Dropdown, para não introduzir um 3º
// primitivo em components/ui sem necessidade.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useState } from 'react';
import { Bell, Check, CheckCheck } from 'lucide-react';
import { useNaoLidasCount, useNotificacoesList, useMarcarLida, useMarcarTodasLidas } from '../hooks/useNotificacoes';
import { TIPO_NOTIFICACAO_LABELS } from '../constants';

export function NotificacaoBell() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: countData } = useNaoLidasCount();
  const { data, isLoading } = useNotificacoesList({ page: 1, limit: 10 });
  const { mutate: marcarLida } = useMarcarLida();
  const { mutate: marcarTodasLidas } = useMarcarTodasLidas();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const naoLidas = countData?.total ?? 0;

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative w-8 h-8 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
        aria-label="Notificações"
      >
        <Bell className="w-4 h-4" />
        {naoLidas > 0 && <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-gold rounded-full" />}
      </button>

      {open && (
        <div className="absolute right-0 top-10 w-80 max-h-96 overflow-y-auto bg-card border border-border rounded-xl shadow-xl z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h3 className="text-sm font-semibold text-foreground">Notificações</h3>
            {naoLidas > 0 && (
              <button
                onClick={() => marcarTodasLidas()}
                className="inline-flex items-center gap-1 text-xs text-gold hover:underline"
              >
                <CheckCheck className="w-3 h-3" />
                Marcar todas como lidas
              </button>
            )}
          </div>
          <div className="divide-y divide-border">
            {isLoading && <div className="p-4 text-sm text-muted-foreground">Carregando...</div>}
            {data?.data.map((n) => (
              <div key={n.id} className={`p-3 flex items-start gap-2 ${!n.lida ? 'bg-accent/30' : ''}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-muted-foreground">
                    {TIPO_NOTIFICACAO_LABELS[n.tipo] ?? n.tipo}
                  </p>
                  <p className="text-sm text-foreground truncate">{n.titulo}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{n.mensagem}</p>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    {new Date(n.createdAt).toLocaleString('pt-BR')}
                  </p>
                </div>
                {!n.lida && (
                  <button
                    onClick={() => marcarLida(n.id)}
                    title="Marcar como lida"
                    className="shrink-0 w-6 h-6 flex items-center justify-center rounded text-muted-foreground hover:text-gold hover:bg-accent transition-colors"
                  >
                    <Check className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
            {!isLoading && (data?.data.length ?? 0) === 0 && (
              <p className="p-4 text-sm text-muted-foreground text-center">Nenhuma notificação.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
